<template>
  <div class="container-fluid py-3">
    <!-- Header -->
    <div class="d-flex align-items-center mb-3">
      <button
        class="btn btn-sm btn-outline-secondary mr-2"
        @click="$router.push({ name: 'despacho' })"
      >
        <feather-icon
          icon="ArrowLeftIcon"
          size="14"
        />
      </button>
      <h4 class="mb-0 flex-grow-1">
        <feather-icon
          icon="TruckIcon"
          size="20"
          class="mr-50"
        />
        Despacho
        <span
          v-if="occurrence"
          class="text-muted font-weight-normal ml-1"
        >
          — {{ occurrence.protocol || '#' + occurrence.id }}
        </span>
      </h4>
      <button
        v-if="occurrence && occurrence.active_responsibility && occurrence.active_responsibility.user_id === $store.state.user.id"
        class="btn btn-sm btn-outline-danger"
        @click="abandonOccurrence"
      >
        <feather-icon
          icon="LogOutIcon"
          size="13"
          class="mr-25"
        />
        Abandonar
      </button>
      <button
        v-if="occurrence && Number(occurrence.status) === 1 && $hasAbility('ocorrencia.finalizar')"
        class="btn btn-sm btn-outline-success ml-1"
        @click="showFinalizeModal = true"
      >
        <feather-icon
          icon="CheckCircleIcon"
          size="13"
          class="mr-25"
        />
        Finalizar
      </button>
    </div>

    <!-- Loading -->
    <div
      v-if="loadingOccurrence"
      class="text-center py-5"
    >
      <b-spinner variant="primary" />
      <p class="mt-2 text-muted">
        Carregando...
      </p>
    </div>

    <!-- Not found -->
    <div
      v-else-if="!occurrence"
      class="text-center py-5 text-muted"
    >
      <feather-icon
        icon="AlertCircleIcon"
        size="40"
        class="mb-2"
      />
      <p>Ocorrência não encontrada.</p>
    </div>

    <div v-else>
      <!-- Info block -->
      <occurrence-info-block
        :occurrence="occurrence"
        :involveds-count="occurrence.involveds ? occurrence.involveds.length : 0"
      />

      <!-- Pipeline horizontal -->
      <div class="occ-htl mb-3">
        <div class="occ-htl-track">
          <template v-for="(step, idx) in pipelineSteps">
            <div
              v-if="idx > 0"
              :key="'line-' + idx"
              class="occ-htl-connector"
              :class="{ 'occ-htl-connector--done': pipelineSteps[idx - 1].state === 'done' && step.state === 'done' }"
              :style="pipelineSteps[idx - 1].state === 'done' && step.state === 'active'
                ? { background: step.variant === 'amber' ? '#F59E0B' : step.variant === 'blue' ? '#2563EB' : '#7367f0' }
                : {}"
            />
            <div
              :key="step.key"
              class="occ-htl-node"
            >
              <div
                class="occ-htl-dot"
                :class="step.state === 'done' ? 'occ-htl-dot--done' : step.state === 'active' ? 'occ-htl-dot--active-' + (step.variant || 'primary') : 'occ-htl-dot--pending'"
              >
                <feather-icon
                  v-if="step.state === 'done'"
                  icon="CheckIcon"
                  size="10"
                  style="color:#fff"
                />
                <feather-icon
                  v-else
                  :icon="step.icon"
                  size="10"
                  :style="{ color: step.state === 'active' ? '#fff' : '#b9b9c3' }"
                />
              </div>
              <span
                class="occ-htl-label"
                :class="step.state === 'done' ? 'occ-htl-label--done' : step.state === 'active' ? 'occ-htl-label--active-' + (step.variant || 'primary') : ''"
              >{{ step.label }}</span>
            </div>
          </template>
        </div>
      </div>

      <!-- Equipes Despachadas -->
      <div class="card">
        <div class="card-header py-2 d-flex align-items-center justify-content-between">
          <h6 class="mb-0">
            <feather-icon
              icon="TruckIcon"
              size="15"
              class="mr-50 text-primary"
            />
            Equipes Despachadas
            <span
              v-if="dispatches.length"
              class="badge badge-primary ml-50"
            >{{ dispatches.length }}</span>
            <span
              v-if="dispatchUpdatesPending"
              class="badge badge-warning ml-50"
              style="cursor: pointer"
              title="Houve mudança nos despachos desta ocorrência"
              @click="applyDispatchUpdates"
            >atualizar</span>
          </h6>
          <button
            class="btn btn-sm btn-success"
            @click="openTeamModal"
          >
            <feather-icon
              icon="PlusIcon"
              size="13"
              class="mr-25"
            />
            Despachar Equipe
          </button>
        </div>

        <div class="card-body p-3">
          <div
            v-if="loadingDispatches"
            class="text-center py-4"
          >
            <b-spinner
              small
              variant="primary"
            />
          </div>
          <div
            v-else-if="dispatches.length === 0"
            class="text-center text-muted py-4"
            style="font-size:13px"
          >
            <feather-icon
              icon="TruckIcon"
              size="28"
              class="mb-1"
            />
            <p class="mb-0">
              Nenhuma equipe despachada ainda.
            </p>
          </div>
          <div
            v-else
            class="dispatch-list"
          >
            <div
              v-for="d in dispatches"
              :key="d.id"
              class="dispatch-card"
              :class="dispatchCardClass(d.status)"
            >
              <!-- Cabeçalho do card -->
              <div class="dispatch-card__head">
                <div>
                  <span class="dispatch-card__team-name">{{ d.team ? d.team.name : '—' }}</span>
                  <span class="dispatch-card__vehicle">
                    <feather-icon
                      icon="TruckIcon"
                      size="11"
                      class="mr-25"
                    />
                    {{ d.team && d.team.vehicle ? d.team.vehicle.name + (d.team.vehicle.plate ? ' · ' + d.team.vehicle.plate : '') : 'Sem veículo' }}
                  </span>
                </div>
                <div
                  class="d-flex align-items-center"
                  style="gap:6px; flex-wrap:wrap"
                >
                  <span
                    class="dispatch-status-badge"
                    :class="'dispatch-status-badge--' + dispatchStatusKey(d.status)"
                  >
                    {{ dispatchStatusLabel(d.status) }}
                  </span>
                  <template v-if="d.status === 1 && $hasAbility('despacho.cancelar')">
                    <button
                      class="btn btn-sm btn-outline-danger"
                      @click="cancelDispatch(d)"
                    >
                      <feather-icon
                        icon="XCircleIcon"
                        size="12"
                        class="mr-25"
                      />
                      Cancelar
                    </button>
                  </template>
                  <span
                    v-else-if="d.cancel_notes || d.complete_notes"
                    class="text-muted"
                    style="font-size:12px;font-style:italic"
                  >
                    "{{ d.cancel_notes || d.complete_notes }}"
                  </span>
                </div>
              </div>

              <!-- Timeline horizontal de posições -->
              <div class="pos-timeline-wrap">
                <div class="pos-timeline">
                  <!-- Evento recebido (fixo) -->
                  <div class="pos-cell pos-cell--reached">
                    <div class="pos-cell__label">
                      Evento<br>recebido
                    </div>
                    <div class="pos-cell__time">
                      {{ formatDateTime(d.created_at) }}
                    </div>
                  </div>

                  <!-- J-codes dinâmicos -->
                  <div
                    v-for="pt in positionTypesOcorrencia"
                    :key="pt.id"
                    class="pos-cell"
                    :class="{
                      'pos-cell--reached': positionReached(d, pt.id),
                      'pos-cell--current': isCurrentPosition(d, pt.id),
                      'pos-cell--pending': d.status === 1 && !positionReached(d, pt.id),
                      'pos-cell--inactive': d.status !== 1 && !positionReached(d, pt.id),
                    }"
                    @click="d.status === 1 && !positionReached(d, pt.id) ? confirmRegisterPosition(d, pt) : null"
                  >
                    <div
                      v-if="pt.code"
                      class="pos-cell__code"
                    >
                      {{ pt.code }}
                    </div>
                    <div class="pos-cell__label">
                      {{ pt.name }}
                    </div>
                    <div class="pos-cell__time">
                      <template v-if="positionReached(d, pt.id)">
                        {{ formatDateTime(positionTime(d, pt.id)) }}
                      </template>
                      <span
                        v-else
                        class="pos-cell__dash"
                      >—</span>
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-if="positionTypesVeiculo.length"
                class="pos-veiculo"
              >
                <span class="pos-veiculo__rotulo">Estado da viatura</span>
                <button
                  v-for="pt in positionTypesVeiculo"
                  :key="pt.id"
                  type="button"
                  class="pos-veiculo__item"
                  :class="{ 'pos-veiculo__item--reached': positionReached(d, pt.id) }"
                  :disabled="d.status !== 1 || positionReached(d, pt.id)"
                  :title="pt.name"
                  @click="confirmRegisterPosition(d, pt)"
                >
                  <strong>{{ pt.code }}</strong>
                  {{ pt.name }}
                  <span
                    v-if="positionReached(d, pt.id)"
                    class="pos-veiculo__hora"
                  >{{ formatDateTime(positionTime(d, pt.id)) }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Modal: Despachar Equipe ──────────────────────────────── -->
    <b-modal
      v-model="showTeamModal"
      hide-footer
      scrollable
      size="xl"
      dialog-class="dispatch-modal"
      @shown="onTeamModalShown"
      @hidden="unwatchFleetPositions"
    >
      <template #modal-title>
        <div class="dispatch-head">
          <span class="dispatch-head__title">Despachar Equipe</span>
          <span
            v-if="occurrence"
            class="dispatch-head__proto"
          >
            {{ occurrence.protocol || '#' + occurrence.id }}
          </span>
          <span
            v-if="occurrence && occurrence.classification"
            class="dispatch-head__risk"
            :style="riskStyle"
          >
            <feather-icon
              icon="AlertCircleIcon"
              size="11"
              class="mr-25"
            />
            {{ occurrence.classification.color_label }}
          </span>
          <span
            v-if="occurrenceWhere"
            class="dispatch-head__where"
            :title="occurrenceWhere"
          >
            <feather-icon
              icon="MapPinIcon"
              size="12"
              class="mr-25"
            />
            {{ occurrenceWhere }}
          </span>
        </div>
      </template>

      <div
        class="dispatch-grid"
        :class="{ 'dispatch-grid--nomap': !hasOccurrenceCoords }"
      >
        <div
          v-if="hasOccurrenceCoords"
          class="dispatch-grid__map"
        >
          <div class="dispatch-grid__canvas">
            <div class="map-ctrl">
              <div class="map-ctrl__pill">
                <span class="map-ctrl__label">Raio</span>
                <div
                  class="radius-group"
                  role="group"
                  aria-label="Raio de busca"
                >
                  <button
                    v-for="option in radiusOptions"
                    :key="option.label"
                    type="button"
                    class="radius-group__btn"
                    :class="{ 'radius-group__btn--on': radiusKm === option.km }"
                    @click="radiusKm = option.km"
                  >{{ option.label }}</button>
                </div>
              </div>
              <span
                v-if="radiusMeters"
                class="map-ctrl__count"
              >
                {{ teamsInRadiusCount }} de {{ fleetPositionsOnMap.length }} dentro · linha reta
              </span>
            </div>

            <GmapMap
              :center="occurrenceLatLng"
              :zoom="13"
              :options="{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }"
              style="width: 100%; height: 100%; border-radius: 6px"
            >
              <GmapMarker
                :position="occurrenceLatLng"
                :z-index="100"
              />
              <GmapCircle
                v-if="radiusMeters"
                :center="occurrenceLatLng"
                :radius="radiusMeters"
                :options="{
                  strokeColor: '#7367f0', strokeOpacity: 0.7, strokeWeight: 1,
                  fillColor: '#7367f0', fillOpacity: 0.06, clickable: false,
                }"
              />
              <GmapMarker
                v-for="position in fleetPositionsOnMap"
                :key="position.identifier_code"
                :position="{ lat: Number(position.lat), lng: Number(position.lng) }"
                :icon="fleetMarkerIcon(position, position.identifier_code === highlightedCode, !inRadius(position))"
                :title="fleetMarkerTitle(position)"
                :z-index="position.identifier_code === highlightedCode ? 90 : (position.stale ? 1 : 10)"
                @click="highlightedCode = position.identifier_code"
              />
            </GmapMap>
          </div>

          <div class="dispatch-map__legend">
            <span><i
              class="fleet-dot"
              style="background:#28c76f"
            />Livre</span>
            <span><i
              class="fleet-dot"
              style="background:#ff9f43"
            />Em atendimento</span>
            <span><i
              class="fleet-dot"
              style="background:#b4bdc6"
            />Posição antiga</span>
            <span
              v-if="fleetPositions.length"
              class="text-muted"
            >
              {{ fleetPositions.length }} viatura{{ fleetPositions.length > 1 ? 's' : '' }} com posição
            </span>
            <span
              v-else-if="!loadingFleetPositions"
              class="text-muted"
            >
              Nenhuma viatura com posição — verifique o código no cadastro da equipe
            </span>

            <div class="dispatch-dist">
              <span
                v-if="loadingDistances"
                class="text-muted"
              >Calculando distâncias...</span>
              <span
                v-else-if="distancesCalculatedAt"
                class="text-muted"
              >
                Distâncias por rota · {{ shortTime(distancesCalculatedAt) }}
              </span>
              <span
                v-else-if="distancesQuotaBlocked"
                class="dispatch-bar__error"
              >
                Cota mensal de distâncias esgotada — mostrando só linha reta
              </span>
              <span
                v-else-if="distancesError"
                class="dispatch-bar__error"
              >{{ distancesError }}</span>
              <span
                v-if="quotaLabel"
                class="text-muted ml-50"
                :class="{ 'text-warning': distancesQuota && distancesQuota.warning }"
                :title="quotaTitle"
              >{{ quotaLabel }}</span>
              <button
                type="button"
                class="dispatch-dist__refresh"
                :disabled="loadingDistances"
                title="Recalcular distâncias"
                @click="loadDistances()"
              >
                <feather-icon
                  icon="RefreshCwIcon"
                  size="12"
                />
              </button>
            </div>
          </div>
        </div>

        <div class="dispatch-grid__list">
          <occurrence-team-requests
            :requests="teamRequests"
            :suggested-types="occurrence && occurrence.team_types ? occurrence.team_types : []"
            :loading="loadingTeamRequests"
          />

          <div
            v-if="!loadingTeamRequests && !teamRequests.length"
            class="dt-no-request"
          >
            <feather-icon
              icon="InfoIcon"
              size="13"
              class="mr-50"
            />
            Nenhuma solicitação médica registrada — o despacho segue livre.
          </div>

          <div
            v-if="loadingTeams"
            class="text-center py-4"
          >
            <b-spinner variant="primary" />
          </div>
          <div
            v-else-if="availableTeams.length === 0"
            class="dt-empty"
          >
            <feather-icon
              icon="UsersIcon"
              size="30"
              class="mb-1"
            />
            <h6 class="mb-50">
              Nenhuma equipe disponível para despacho
            </h6>
            <p class="mb-0">
              Uma equipe só aparece aqui quando está com embarque aberto, em uma posição
              que permite despacho, e sem despacho ativo em outra ocorrência.
            </p>
          </div>
          <div
            v-else
            class="team-list"
          >
            <div
              v-for="team in sortedTeams"
              :key="team.id"
              class="team-card"
              :class="{
                'team-card--match': requestedTeamTypeId && team.type_id === requestedTeamTypeId,
                'team-card--highlight': team.frotas_identifier_code && team.frotas_identifier_code === highlightedCode,
              }"
              :title="teamTooltip(team)"
              @mouseenter="highlightedCode = team.frotas_identifier_code || null"
              @mouseleave="highlightedCode = null"
            >
              <div class="team-card__info">
                <div class="team-card__name">
                  {{ team.name }}
                  <span
                    v-if="requestedTeamTypeId && team.type_id === requestedTeamTypeId"
                    class="team-card__badge"
                  >
                    <feather-icon
                      icon="CheckIcon"
                      size="10"
                      class="mr-25"
                    />solicitado
                  </span>
                  <span
                    v-if="team.type"
                    class="team-card__type"
                  >{{ team.type.name }}</span>
                </div>
                <div class="team-card__meta">
                  <feather-icon
                    icon="TruckIcon"
                    size="11"
                    class="mr-25"
                  />
                  {{ teamVehicleLabel(team) }}
                  <span
                    v-if="teamPositionAge(team)"
                    class="team-card__age"
                  >· {{ teamPositionAge(team) }}</span>

                  <template v-if="teamDistance(team)">
                    <span class="team-card__sep">·</span>
                    <span class="team-card__dist">{{ teamDistance(team).distance_text }}</span>
                  </template>
                  <template v-else-if="loadingDistances">
                    <span class="team-card__sep">·</span>
                    <span class="text-muted">calculando...</span>
                  </template>
                  <template v-else>
                    <span class="team-card__sep">·</span>
                    <span class="text-muted">sem distância</span>
                  </template>

                  <span
                    v-if="radiusMeters && teamStraightKm(team) !== null"
                    class="team-card__ring"
                    :class="{ 'team-card__ring--out': teamStraightKm(team) > radiusKm }"
                  >{{ teamStraightKm(team) > radiusKm ? 'fora do raio' : 'no raio' }}</span>
                </div>

                <div
                  v-if="teamDistance(team)"
                  class="team-card__etas"
                >
                  <feather-icon
                    icon="ClockIcon"
                    size="11"
                    class="team-card__etas-icon"
                  />
                  <span
                    v-if="teamDistance(team).has_traffic"
                    class="team-card__eta"
                  >
                    <span class="team-card__eta-label">com trânsito</span>
                    <b>{{ etaLabel(teamDistance(team).seconds) }}</b>
                  </span>
                  <span
                    v-else
                    class="team-card__eta"
                  >
                    <b>{{ etaLabel(teamDistance(team).seconds) }}</b>
                  </span>
                  <span
                    v-if="teamDistance(team).has_traffic"
                    class="team-card__eta team-card__eta--free"
                  >
                    <span class="team-card__eta-label">sem trânsito</span>
                    <span>{{ etaLabel(teamDistance(team).free_seconds) }}</span>
                  </span>
                </div>
              </div>
              <button
                v-if="$hasAbility('despacho.criar')"
                class="btn btn-sm btn-success ml-2"
                :disabled="dispatching === team.id"
                @click="dispatchTeam(team)"
              >
                <b-spinner
                  v-if="dispatching === team.id"
                  small
                  class="mr-25"
                />
                <feather-icon
                  v-else
                  icon="SendIcon"
                  size="13"
                  class="mr-25"
                />
                Despachar
              </button>
            </div>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- ── Modal: Divergência de tipo ───────────────────────────── -->
    <b-modal
      v-model="showDivergenceModal"
      title="Tipo diferente do solicitado"
      ok-title="Despachar mesmo assim"
      cancel-title="Voltar"
      ok-variant="warning"
      :ok-disabled="savingDivergence || divergenceReason.trim().length < 10"
      @ok="confirmDivergentDispatch"
    >
      <p class="mb-1">
        A regulação solicitou <strong>{{ requestedTeamTypeName }}</strong> e a equipe
        <strong>{{ divergentTeam ? divergentTeam.name : '' }}</strong> é
        <strong>{{ divergentTeam && divergentTeam.type ? divergentTeam.type.name : 'de tipo não definido' }}</strong>.
      </p>
      <div class="form-group mb-0">
        <label><span class="required">*</span> Motivo da divergência:</label>
        <textarea
          v-model="divergenceReason"
          class="form-control"
          rows="3"
          maxlength="1000"
          placeholder="Ex: nenhuma USA disponível na região no momento"
        />
        <small class="text-muted">Mínimo de 10 caracteres. Fica registrado na auditoria.</small>
      </div>
    </b-modal>

    <!-- ── Modal: Cancelar Despacho ─────────────────────────────── -->
    <b-modal
      v-model="showCancelModal"
      title="Cancelar Despacho"
      ok-title="Confirmar Cancelamento"
      cancel-title="Voltar"
      ok-variant="danger"
      :ok-disabled="savingAction"
      @ok="confirmCancel"
    >
      <div class="form-group">
        <label>Motivo do cancelamento (opcional):</label>
        <textarea
          v-model="actionNotes"
          class="form-control"
          rows="3"
          placeholder="Descreva o motivo..."
          maxlength="500"
        />
      </div>
    </b-modal>

    <FinalizeOccurrenceModal
      :visible.sync="showFinalizeModal"
      :occurrence-id="occurrence ? occurrence.id : null"
      :discipline-id="occurrence && occurrence.discipline_id ? occurrence.discipline_id : null"
      :active-dispatches="activeDispatchesCount"
      @finalized="onFinalized"
      @open-pendency="openPendency"
    />

    <BusinessRulePendenciesModal
      :payload="rulePendencies"
      :retrying="ruleRetrying"
      title="Não é possível despachar"
      @retry="executeGuarded"
      @close="closeRulePendencies"
      @open="openPendency"
    />
  </div>
</template>

<script>
import ApiErrorMixin from '@/mixins/ApiErrorMixin'
import TeamRequestsMixin from '@/mixins/TeamRequestsMixin'
import OccurrenceInfoBlock from '@/components/OccurrenceInfoBlock.vue'
import OccurrenceTeamRequests from '@/components/OccurrenceTeamRequests.vue'
import FinalizeOccurrenceModal from '@/components/FinalizeOccurrenceModal.vue'
import BusinessRuleGuardMixin from '@/mixins/BusinessRuleGuardMixin'
import BusinessRulePendenciesModal from '@/components/BusinessRulePendenciesModal.vue'
import FleetPositionsMixin from '@/mixins/FleetPositionsMixin'
import ElapsedTimeMixin from '@/mixins/ElapsedTimeMixin'
import RealtimeResyncMixin from '@/mixins/RealtimeResyncMixin'
import DispatchEventsMixin from '@/mixins/DispatchEventsMixin'

export default {
  name: 'DespachoDetail',
  components: {
    OccurrenceInfoBlock, OccurrenceTeamRequests, FinalizeOccurrenceModal, BusinessRulePendenciesModal,
  },
  mixins: [
    ElapsedTimeMixin,
    ApiErrorMixin,
    TeamRequestsMixin,
    FleetPositionsMixin,
    RealtimeResyncMixin,
    DispatchEventsMixin,
    BusinessRuleGuardMixin,
  ],

  data() {
    return {
      loadingOccurrence: true,
      occurrence: null,

      dispatches: [],
      loadingDispatches: false,
      dispatchUpdatesPending: 0,

      availableTeams: [],
      loadingTeams: false,
      dispatching: null,
      showTeamModal: false,
      // Código da viatura sob o cursor — liga o card ao pino nos dois sentidos.
      highlightedCode: null,

      distances: {},
      distancesCalculatedAt: null,
      distancesError: null,
      loadingDistances: false,

      distancesBasis: {},
      distancesAutoRefresh: false,
      distancesQuota: null,
      distancesQuotaBlocked: false,
      distancesLastAttempt: 0,

      // null = sem filtro. O padrão mostra tudo: esconder viatura por padrão
      // numa tela de despacho seria decidir pelo despachador.
      radiusKm: null,
      radiusOptions: [
        { label: 'Tudo', km: null },
        { label: '2 km', km: 2 },
        { label: '5 km', km: 5 },
        { label: '10 km', km: 10 },
      ],

      positionTypes: [],
      loadingPositionTypes: false,

      showCancelModal: false,
      showFinalizeModal: false,
      actionNotes: '',
      savingAction: false,
      activeDispatch: null,

      showDivergenceModal: false,
      divergentTeam: null,
      divergenceReason: '',
      savingDivergence: false,
    }
  },

  computed: {
    positionTypesOcorrencia() {
      return this.positionTypes.filter(pt => (pt.scope || 'ocorrencia') !== 'veiculo')
    },

    positionTypesVeiculo() {
      return this.positionTypes.filter(pt => pt.scope === 'veiculo')
    },

    quotaLabel() {
      const q = this.distancesQuota
      if (!q || q.cap === null || q.cap === undefined) return ''

      return `cota ${this.milhar(q.used)}/${this.milhar(q.cap)} · restam ${this.milhar(q.remaining)}`
    },

    quotaTitle() {
      const q = this.distancesQuota
      if (!q || q.cap === null || q.cap === undefined) return ''

      return `Elementos do Distance Matrix consumidos neste mês: ${this.milhar(q.used)} de ${this.milhar(q.cap)}.`
    },

    anyModalOpen() {
      return this.showTeamModal
        || this.showCancelModal
        || this.showFinalizeModal
        || this.showDivergenceModal
    },

    activeDispatchesCount() {
      return this.dispatches.filter(d => Number(d.status) === 1).length
    },

    hasOccurrenceCoords() {
      return !!(this.occurrence && this.occurrence.latitude && this.occurrence.longitude)
    },

    riskStyle() {
      const c = (this.occurrence && this.occurrence.classification
        && this.occurrence.classification.hex_color) || '#7367f0'

      return { backgroundColor: `${c}18`, borderColor: c, color: c }
    },

    occurrenceWhere() {
      if (!this.occurrence) return ''

      const local = [this.occurrence.neighborhood, this.occurrence.city]
        .filter(Boolean).join(' · ')

      return [this.occurrence.address, local].filter(Boolean).join(' — ')
    },

    occurrenceLatLng() {
      if (!this.hasOccurrenceCoords) return { lat: 0, lng: 0 }
      return {
        lat: parseFloat(this.occurrence.latitude),
        lng: parseFloat(this.occurrence.longitude),
      }
    },

    radiusMeters() {
      return this.radiusKm ? this.radiusKm * 1000 : null
    },

    teamsInRadiusCount() {
      return this.fleetPositionsOnMap.filter(p => this.inRadius(p)).length
    },

    /** Só as de dentro do raio vão para o Google — cota não se gasta com quem não seria despachado. */
    codesForDistance() {
      const dentro = this.fleetPositionsOnMap.filter(p => this.inRadius(p))
      return dentro.map(p => p.identifier_code)
    },

    /** Posição por código, para o card achar a sua sem varrer o array. */
    fleetByCode() {
      return this.fleetPositions.reduce((mapa, p) => {
        mapa[p.identifier_code] = p
        return mapa
      }, {})
    },

    sortedTeams() {
      const wanted = this.requestedTeamTypeId

      // Dois critérios, nesta ordem. A regulação pediu um tipo por um motivo
      // clínico — proximidade refina a escolha dentro do que ela autorizou,
      // não promove uma equipe de outro tipo acima dela.
      return [...this.availableTeams].sort((a, b) => {
        if (wanted) {
          const aMatch = a.type_id === wanted ? 0 : 1
          const bMatch = b.type_id === wanted ? 0 : 1
          if (aMatch !== bMatch) return aMatch - bMatch
        }

        const aSec = this.teamSeconds(a)
        const bSec = this.teamSeconds(b)

        // Sem distância vai para o fim do SEU grupo, nunca da lista: continua
        // sendo do tipo que a regulação pediu.
        if (aSec !== bSec) return aSec - bSec

        return (a.name || '').localeCompare(b.name || '')
      })
    },

    pipelineSteps() {
      if (!this.occurrence) return []
      const hasDispatches = this.dispatches.length > 0
      const stepToPos = {
        solicita: 0, triagem: 0, regulacao: 1, despacha: 2, em_atendimento: 3, encerrado: 4,
      }
      const currentPos = stepToPos[this.occurrence.current_step] ?? 0
      const despachaActive = currentPos === 2
      // ocorrência voltou de despacha (ex: após releases_occurrence) mas despachos existem
      const despachaDone = hasDispatches && currentPos < 2
      const steps = [
        {
          key: 'solicita', label: 'Solicitação', icon: 'FileTextIcon', pos: 0,
        },
        {
          key: 'regulacao', label: 'Regulação', icon: 'ActivityIcon', pos: 1,
        },
        {
          key: 'despacha',
          label: despachaActive
            ? (hasDispatches ? 'Despachado' : 'Aguardando Despacho')
            : (despachaDone ? 'Despachado' : 'Despacho'),
          icon: despachaActive && !hasDispatches ? 'ClockIcon' : 'TruckIcon',
          pos: 2,
          variant: despachaActive ? (hasDispatches ? 'blue' : 'amber') : null,
        },
        {
          key: 'finalizado', label: 'Finalizado', icon: 'CheckCircleIcon', pos: 3,
        },
      ]
      return steps.map(s => ({
        ...s,
        state: s.pos < currentPos
          ? 'done'
          : s.pos === currentPos
            ? 'active'
            : (s.key === 'despacha' && despachaDone ? 'done' : 'pending'),
      }))
    },
  },

  watch: {
    /**
     * Aumentar o raio traz viaturas que ainda não têm número — busca só essas.
     * Diminuir não recalcula nada: os números que já estão na tela continuam
     * válidos, e refazer a chamada gastaria cota para não mudar nada.
     */
    radiusKm() {
      const faltando = this.codesForDistance.some(code => !this.distances[code])
      if (faltando) this.loadDistances()
    },

    anyModalOpen(aberto) {
      if (!aberto && this.dispatchUpdatesPending) this.applyDispatchUpdates()
    },
  },

  async mounted() {
    await this.loadOccurrence()
    this.assumeOccurrence()
    this.watchTeamRequests()
    this.watchDispatchEvents()
    this.watchRealtimeResync(() => this.applyDispatchUpdates())
  },

  methods: {
    // ── Occurrence ───────────────────────────────────────────────────

    async assumeOccurrence() {
      if (!this.occurrence) return
      try {
        const res = await this.$http.post(`${this.$store.state.api}occurrences/${this.occurrence.id}/assume`)
        this.$set(this.occurrence, 'active_responsibility', res.data.data)
      } catch (_) {
        // Assumir de novo o que ja e seu devolve erro, e nao e problema
      }
    },

    onFinalized() {
      this.showFinalizeModal = false
      this.$message('Sucesso', 'Ocorrência finalizada.', 'success')
      this.$router.push({ name: 'despacho' })
    },

    // Os painéis do envolvido vivem na Regulação; daqui o atalho leva para lá
    // já com o envolvido e a aba certos.
    openPendency(violation) {
      this.closeRulePendencies()
      this.showFinalizeModal = false

      this.$router.push({
        name: 'regulacao-detail',
        params: { id: this.occurrence.id },
        query: {
          involved: violation.occurrence_involved_id,
          panel: violation.target,
        },
      })
    },

    async abandonOccurrence() {
      try {
        await this.$http.post(`${this.$store.state.api}occurrences/${this.occurrence.id}/abandon`)
        this.$router.push({ name: 'despacho' })
      } catch (e) {
        this.$message('Erro', this.apiErrorText(e, 'Erro ao abandonar ocorrência.'), 'error')
      }
    },

    async loadOccurrence() {
      this.loadingOccurrence = true
      try {
        const { id } = this.$route.params
        const res = await this.$http.get(`${this.$store.state.api}occurrences/${id}`, {
          params: { with: ['involveds', 'dispatchType', 'dispatchReason', 'discipline', 'classification', 'createdBy'] },
        })
        this.occurrence = res.data.data || res.data
        await Promise.all([
          this.loadDispatches(),
          this.loadPositionTypes(),
          this.loadTeamRequests(this.occurrence.id),
        ])
      } catch (e) {
        this.$message('Erro', 'Erro ao carregar ocorrência.', 'error')
      } finally {
        this.loadingOccurrence = false
      }
    },

    // ── Dispatches ───────────────────────────────────────────────────

    onDispatchEvent(kind, payload) {
      if (!this.occurrence) return
      if (Number(payload.occurrence_id) !== Number(this.occurrence.id)) return

      if (this.anyModalOpen) {
        this.dispatchUpdatesPending += 1
        return
      }

      this.scheduleRealtimeReload(() => this.applyDispatchUpdates())
    },

    async applyDispatchUpdates() {
      if (!this.occurrence) return

      this.dispatchUpdatesPending = 0
      await this.loadDispatches()
    },

    async loadDispatches() {
      this.loadingDispatches = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}occurrence_dispatches`, {
          params: { occurrence_id: this.occurrence.id },
        })
        this.dispatches = res.data.data || res.data || []
      } catch (e) {
        this.dispatches = []
      } finally {
        this.loadingDispatches = false
      }
    },

    // ── Teams modal ──────────────────────────────────────────────────

    async openTeamModal() {
      this.showTeamModal = true
      await this.loadAvailableTeams()
    },

    async onTeamModalShown() {
      this.watchFleetPositions()
      await this.loadFleetPositions()
      // Depois das posições: sem elas não há o que medir.
      this.loadDistances()
    },

    async loadDistances({ auto = false } = {}) {
      if (!this.occurrence || !this.hasOccurrenceCoords) return

      this.distancesLastAttempt = Date.now()
      this.loadingDistances = true
      this.distancesError = null

      try {
        const res = await this.$http.post(
          `${this.$store.state.api}ocorrencias/${this.occurrence.id}/distancias`,
          this.radiusMeters ? { codes: this.codesForDistance } : {},
        )
        this.distances = res.data.distances || {}
        this.distancesCalculatedAt = res.data.calculated_at || null
        this.distancesAutoRefresh = res.data.auto_refresh === true
        this.distancesQuota = res.data.quota || null
        this.distancesQuotaBlocked = res.data.quota_blocked === true
        this.distancesBasis = this.snapshotPositions()
      } catch (e) {
        // A lista segue sem a coluna. Um despacho não trava porque o Google
        // não respondeu.
        if (!auto) {
          this.distances = {}
          this.distancesCalculatedAt = null
        }
        this.distancesError = this.apiErrorText(e, 'Distâncias indisponíveis')
      } finally {
        this.loadingDistances = false
      }
    },

    snapshotPositions() {
      const retrato = {}

      this.codesForDistance.forEach(code => {
        const p = this.fleetByCode[code]
        if (p && p.lat !== null && p.lng !== null) {
          retrato[code] = { lat: Number(p.lat), lng: Number(p.lng) }
        }
      })

      return retrato
    },

    onFleetPositionsApplied() {
      if (!this.distancesAutoRefresh) return
      if (!this.showTeamModal || !this.hasOccurrenceCoords) return
      if (this.loadingDistances) return
      if (document.hidden) return
      if (Date.now() - this.distancesLastAttempt < 30000) return

      const mexeu = this.codesForDistance.some(code => {
        const p = this.fleetByCode[code]
        if (!p || p.lat === null || p.lng === null) return false

        const antes = this.distancesBasis[code]
        if (!antes) return true

        const km = this.fleetStraightLineKm(antes.lat, antes.lng, Number(p.lat), Number(p.lng))
        return km > 0.2
      })

      if (mexeu) this.loadDistances({ auto: true })
    },

    teamDistance(team) {
      if (!team.frotas_identifier_code) return null
      return this.distances[team.frotas_identifier_code] || null
    },

    /** Segundos até a ocorrência, ou infinito quando não há número para ordenar. */
    teamSeconds(team) {
      const d = this.teamDistance(team)
      return d ? d.seconds : Number.MAX_SAFE_INTEGER
    },

    teamStraightKm(team) {
      const p = team.frotas_identifier_code ? this.fleetByCode[team.frotas_identifier_code] : null
      if (!p || p.lat === null || p.lng === null) return null
      return this.fleetStraightLineKm(
        this.occurrenceLatLng.lat, this.occurrenceLatLng.lng, Number(p.lat), Number(p.lng),
      )
    },

    /** Sem raio escolhido, tudo está dentro. */
    inRadius(position) {
      if (!this.radiusKm) return true
      return this.fleetStraightLineKm(
        this.occurrenceLatLng.lat, this.occurrenceLatLng.lng,
        Number(position.lat), Number(position.lng),
      ) <= this.radiusKm
    },

    milhar(n) {
      if (n === null || n === undefined) return '—'
      return Number(n).toLocaleString('pt-BR')
    },

    shortTime(iso) {
      if (!iso) return ''
      return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    },

    /**
     * `main.vehicles` está vazia por decisão de projeto — o vínculo com a
     * viatura vive no código do frotas. Escrever "Sem veículo" numa equipe que
     * tem USA-21 vinculada seria mentira numa tela de despacho.
     */
    teamVehicleLabel(team) {
      if (team.vehicle) {
        return team.vehicle.name + (team.vehicle.plate ? ` · ${team.vehicle.plate}` : '')
      }
      if (team.frotas_identifier_code) return team.frotas_identifier_code
      return 'Sem viatura vinculada'
    },

    /** Só aparece quando a leitura envelheceu — em viatura fresca é ruído. */
    teamPositionAge(team) {
      const p = team.frotas_identifier_code ? this.fleetByCode[team.frotas_identifier_code] : null
      if (!p || !p.stale) return null
      return this.fleetAgeLabel(p)
    },

    etaLabel(seconds) {
      if (seconds === null || seconds === undefined) return '—'

      const min = Math.round(seconds / 60)
      if (min < 60) return `${min} min`

      const h = Math.floor(min / 60)
      const resto = min % 60

      return resto ? `${h} h ${resto} min` : `${h} h`
    },

    teamTooltip(team) {
      const linhas = []

      if (team.type) linhas.push(`Tipo: ${team.type.name}`)

      if (team.active_boarding && team.active_boarding.professionals_count) {
        const n = team.active_boarding.professionals_count
        linhas.push(`${n} profissional${n > 1 ? 'is' : ''} embarcado${n > 1 ? 's' : ''}`)
      }

      if (team.current_position_type) {
        linhas.push(`Posição: ${team.current_position_type.code} · ${team.current_position_type.name}`)
      }

      return linhas.join('\n')
    },

    // O backend já devolve só as equipes despacháveis do nó — embarque aberto,
    // posição que permite despacho e sem despacho ativo em nenhuma ocorrência.
    // Antes havia um filtro aqui contra os despachos da ocorrência aberta, que
    // deixava a mesma equipe aparecer como livre nas demais ocorrências.
    async loadAvailableTeams() {
      this.loadingTeams = true
      try {
        const res = await this.$http.post(`${this.$store.state.api}team_availables`)
        this.availableTeams = res.data.data || []
      } catch (e) {
        this.availableTeams = []
        this.$message('Erro', this.apiErrorText(e, 'Não foi possível consultar as equipes disponíveis.'), 'error')
      } finally {
        this.loadingTeams = false
      }
    },

    dispatchTeam(team) {
      const wanted = this.requestedTeamTypeId
      const diverges = !!wanted && team.type_id !== wanted

      if (diverges) {
        this.divergentTeam = team
        this.divergenceReason = ''
        this.showDivergenceModal = true
        return
      }

      this.sendDispatch(team, null)
    },

    async confirmDivergentDispatch(bvModalEvent) {
      bvModalEvent.preventDefault()
      this.savingDivergence = true
      try {
        await this.sendDispatch(this.divergentTeam, this.divergenceReason.trim())
        this.showDivergenceModal = false
        this.divergentTeam = null
      } catch (e) {
        // sendDispatch ja mostrou a mensagem ao usuario
      } finally {
        this.savingDivergence = false
      }
    },

    async sendDispatch(team, divergenceReason) {
      this.dispatching = team.id
      try {
        const pending = this.pendingTeamRequests[0]

        await this.runGuarded(
          options => this.$http.post(`${this.$store.state.api}occurrence_dispatches`, {
            occurrence_id: this.occurrence.id,
            team_id: team.id,
            occurrence_team_request_id: pending ? pending.id : null,
            type_divergence_reason: divergenceReason,
            ...options,
          }),
          async res => {
            this.dispatches.push(res.data.data || res.data)
            this.showTeamModal = false
            this.$message('Despacho', `Equipe ${team.name} despachada.`, 'success')

            await Promise.all([
              this.loadTeamRequests(this.occurrence.id),
              this.loadAvailableTeams(),
            ])
          },
        )
      } catch (e) {
        this.$message('Erro', this.apiErrorText(e, 'Erro ao despachar equipe.'), 'error')
        throw e
      } finally {
        this.dispatching = null
      }
    },

    // ── Position types & timeline ────────────────────────────────────

    async loadPositionTypes() {
      if (this.positionTypes.length) return
      this.loadingPositionTypes = true
      try {
        const res = await this.$http.get(`${this.$store.state.api}team_position_types/for-node`, {
          params: { discipline_id: this.occurrence?.discipline_id },
        })
        this.positionTypes = res.data.data || res.data || []
      } catch (e) {
        this.positionTypes = []
      } finally {
        this.loadingPositionTypes = false
      }
    },

    positionReached(dispatch, positionTypeId) {
      return !!(dispatch.positions && dispatch.positions.some(p => p.position_type_id === positionTypeId))
    },

    positionTime(dispatch, positionTypeId) {
      if (!dispatch.positions) return null
      const pos = dispatch.positions.find(p => p.position_type_id === positionTypeId)
      return pos ? pos.reported_at : null
    },

    isCurrentPosition(dispatch, positionTypeId) {
      if (!dispatch.positions || !dispatch.positions.length) return false
      const latest = dispatch.positions.reduce((a, b) => (new Date(a.reported_at) > new Date(b.reported_at) ? a : b))
      return latest.position_type_id === positionTypeId
    },

    async confirmRegisterPosition(dispatch, positionType) {
      const label = positionType.code
        ? `${positionType.code} — ${positionType.name}`
        : positionType.name

      const confirmed = await swal({
        title: 'Registrar posição?',
        text: label,
        icon: 'info',
        buttons: {
          cancel: { text: 'Cancelar', value: false, visible: true },
          confirm: { text: 'Confirmar', value: true },
        },
      })

      if (!confirmed) return

      try {
        const res = await this.$http.post(`${this.$store.state.api}team_positions`, {
          team_id: dispatch.team_id,
          position_type_id: positionType.id,
          occurrence_dispatch_id: dispatch.id,
          reported_at: new Date().toISOString(),
        })
        const saved = res.data.data || res.data
        const d = this.dispatches.find(x => x.id === dispatch.id)
        if (d) {
          if (!d.positions) this.$set(d, 'positions', [])
          d.positions.push(saved)
          // se a posição liberou o despacho, recarrega para refletir status=3
          if (saved.position_type && saved.position_type.releases_occurrence) {
            await this.loadDispatches()
          }
        }
      } catch (e) {
        swal('Erro', e.response?.data?.message || 'Não foi possível registrar a posição.', 'error')
      }
    },

    // ── Cancel dispatch ──────────────────────────────────────────────

    cancelDispatch(dispatch) {
      this.activeDispatch = dispatch
      this.actionNotes = ''
      this.showCancelModal = true
    },

    async confirmCancel(bvModalEvent) {
      bvModalEvent.preventDefault()
      this.savingAction = true
      try {
        const res = await this.$http.post(`${this.$store.state.api}occurrence_dispatches/${this.activeDispatch.id}/cancel`, {
          notes: this.actionNotes || null,
        })
        const updated = res.data.data || res.data
        const idx = this.dispatches.findIndex(d => d.id === this.activeDispatch.id)
        if (idx !== -1) this.$set(this.dispatches, idx, updated)
        this.showCancelModal = false

        await this.loadTeamRequests(this.occurrence.id)
      } catch (e) {
        this.$message('Erro', this.apiErrorText(e, 'Erro ao cancelar despacho.'), 'error')
      } finally {
        this.savingAction = false
      }
    },

    // ── Helpers ──────────────────────────────────────────────────────

    dispatchStatusLabel(status) {
      return { 1: 'Ativo', 2: 'Cancelado', 3: 'Concluído' }[status] || '—'
    },

    dispatchStatusKey(status) {
      return { 1: 'active', 2: 'cancelled', 3: 'completed' }[status] || 'active'
    },

    dispatchCardClass(status) {
      return { 1: 'dispatch-card--active', 2: 'dispatch-card--cancelled', 3: 'dispatch-card--completed' }[status] || ''
    },

    formatDateTime(val) {
      if (!val) return '—'
      return new Date(val).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style>
.modal.show .modal-dialog.dispatch-modal,
.modal-dialog.dispatch-modal {
  width: 96%;
  max-width: 96%;
  margin-left: auto;
  margin-right: auto;
}

.dispatch-modal .modal-content {
  height: calc(100vh - 3.5rem);
}

.modal-dialog.dispatch-modal .modal-body {
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

@media (max-width: 1199.98px) {
  .dispatch-modal .modal-content {
    height: auto;
  }
  .modal-dialog.dispatch-modal .modal-body {
    overflow-y: auto;
  }
}

@media (max-height: 560px) {
  .dispatch-modal .modal-content {
    height: auto;
  }
  .modal-dialog.dispatch-modal .modal-body {
    overflow-y: auto;
  }
}
</style>

<style scoped>
/* ── Pipeline ──────────────────────────────────────────────────── */
.occ-htl { background:#fff; border:1px solid #ebe9f1; border-radius:10px; padding:16px 24px; overflow-x:auto; }
.occ-htl-track { display:flex; align-items:center; min-width:420px; }
.occ-htl-connector { flex:1; height:2px; background:#e0dfe8; min-width:12px; flex-shrink:1; }
.occ-htl-connector--done { background:#28c76f; }
.occ-htl-node { display:flex; flex-direction:column; align-items:center; gap:7px; flex-shrink:0; }
.occ-htl-dot { width:32px; height:32px; border-radius:50%; border:2px solid #d8d6de; background:#fff; display:flex; align-items:center; justify-content:center; }
.occ-htl-dot--done { background:#28c76f; border-color:#28c76f; }
.occ-htl-dot--active-primary { background:#7367f0; border-color:#7367f0; animation:htl-pulse-primary 2s ease-in-out infinite; }
.occ-htl-dot--active-amber   { background:#f59e0b; border-color:#f59e0b; animation:htl-pulse-amber   2s ease-in-out infinite; }
.occ-htl-dot--active-blue    { background:#2563eb; border-color:#2563eb; animation:htl-pulse-blue    2s ease-in-out infinite; }
@keyframes htl-pulse-primary { 0%,100%{box-shadow:0 0 0 0 rgba(115,103,240,.3)} 50%{box-shadow:0 0 0 6px rgba(115,103,240,0)} }
@keyframes htl-pulse-amber   { 0%,100%{box-shadow:0 0 0 0 rgba(245,158,11,.3)}  50%{box-shadow:0 0 0 6px rgba(245,158,11,0)} }
@keyframes htl-pulse-blue    { 0%,100%{box-shadow:0 0 0 0 rgba(37,99,235,.3)}   50%{box-shadow:0 0 0 6px rgba(37,99,235,0)} }
.occ-htl-label { font-size:10px; font-weight:600; color:#b9b9c3; text-align:center; white-space:nowrap; text-transform:uppercase; letter-spacing:.3px; max-width:80px; }
.occ-htl-label--done           { color:#28c76f; }
.occ-htl-label--active-primary { color:#7367f0; }
.occ-htl-label--active-amber   { color:#d97706; }
.occ-htl-label--active-blue    { color:#2563eb; }
.dark-layout .occ-htl { background:#283046; border-color:#3b4253; }
.dark-layout .occ-htl-connector { background:#3b4253; }
.dark-layout .occ-htl-dot { background:#283046; border-color:#3b4253; }

/* ── Dispatch cards ─────────────────────────────────────────────── */
.dispatch-list { display:flex; flex-direction:column; gap:12px; }

.dispatch-card { border:1px solid #ebe9f1; border-left:4px solid #7367f0; border-radius:8px; background:#fff; overflow:hidden; }
.dispatch-card--active    { border-left-color:#2563eb; }
.dispatch-card--cancelled { border-left-color:#6b7280; }
.dispatch-card--completed { border-left-color:#28c76f; }

.dispatch-card__head { display:flex; align-items:center; justify-content:space-between; padding:10px 14px; flex-wrap:wrap; gap:8px; }
.dispatch-card__team-name { font-size:14px; font-weight:700; color:#5e5873; display:block; }
.dispatch-card__vehicle { display:flex; align-items:center; font-size:11px; color:#b9b9c3; margin-top:2px; }

.dispatch-status-badge { display:inline-flex; align-items:center; padding:2px 9px; border-radius:20px; font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.3px; }
.dispatch-status-badge--active    { background:rgba(37,99,235,.12);   color:#2563eb; }
.dispatch-status-badge--cancelled { background:rgba(107,114,128,.12); color:#6b7280; }
.dispatch-status-badge--completed { background:rgba(40,199,111,.12);  color:#28c76f; }

/* ── Position timeline ──────────────────────────────────────────── */
.pos-timeline-wrap { overflow-x:auto; border-top:1px solid #ebe9f1; }

.pos-veiculo {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #ebe9f1;
}
.pos-veiculo__rotulo {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #b9b9c3;
}
.pos-veiculo__item {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  padding: 4px 10px;
  border: 1px solid #ebe9f1;
  border-radius: 14px;
  background: #fff;
  font-size: 11.5px;
  color: #6e6b7b;
  cursor: pointer;
}
.pos-veiculo__item:hover:not(:disabled) { border-color: #7367f0; color: #7367f0; }
.pos-veiculo__item:disabled { cursor: default; }
.pos-veiculo__item--reached {
  border-color: #28c76f;
  color: #28c76f;
  background: rgba(40, 199, 111, 0.08);
}
.pos-veiculo__hora { font-size: 10px; opacity: 0.75; }
.dark-layout .pos-veiculo { border-top-color: #3b4253; }
.dark-layout .pos-veiculo__item { background: #283046; border-color: #3b4253; color: #b4b7bd; }

.pos-timeline {
  display: flex;
  flex-direction: row;
  min-width: max-content;
}

.pos-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 108px;
  min-width: 108px;
  padding: 8px 6px;
  border-right: 1px solid #ebe9f1;
  text-align: center;
  transition: background .15s;
  position: relative;
}

.pos-cell:last-child { border-right: none; }

.pos-cell--reached {
  background: #1e3a5f;
  color: #fff;
}

.pos-cell--current {
  background: #2563eb !important;
  animation: pos-pulse 1.8s ease-in-out infinite;
}

@keyframes pos-pulse {
  0%,100% { box-shadow: 0 0 0 0 rgba(37,99,235,.4); }
  50%      { box-shadow: 0 0 0 5px rgba(37,99,235,0); }
}

.pos-cell--pending {
  background: #f8f9fa;
  cursor: pointer;
}

.pos-cell--pending:hover {
  background: #e8f0fe;
  border-color: #2563eb;
}

.pos-cell--inactive {
  background: #f8f9fa;
  cursor: default;
  opacity: .6;
}

.pos-cell__code {
  font-size: 10px;
  font-weight: 700;
  opacity: .75;
  margin-bottom: 2px;
  line-height: 1;
}

.pos-cell__label {
  font-size: 10px;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 4px;
}

.pos-cell--reached .pos-cell__label,
.pos-cell--reached .pos-cell__code { color: #fff; }
.pos-cell--reached .pos-cell__time { color: rgba(255,255,255,.85); }

.pos-cell--pending .pos-cell__label { color: #6b7280; }

.pos-cell__time {
  font-size: 10px;
  font-weight: 500;
  line-height: 1.2;
}

.pos-cell__dash { color: #d1d5db; font-size: 13px; }

/* ── Team modal ──────────────────────────────────────────────────── */

.dispatch-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) clamp(340px, 26vw, 460px);
  gap: 16px;
  flex: 1;
  min-height: 0;
}

.dispatch-grid--nomap {
  grid-template-columns: minmax(0, 1fr);
}

.dispatch-grid__map {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.dispatch-grid__canvas {
  position: relative;
  flex: 1;
  min-height: 240px;
}

.map-ctrl {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
  pointer-events: none;
}
.map-ctrl__pill {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 5px 9px;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 1px 4px rgba(34, 41, 47, .25);
  pointer-events: auto;
}
.map-ctrl__label { color: #6e6b7b; font-weight: 600; font-size: 11.5px; }
.map-ctrl__count {
  padding: 2px 7px;
  border-radius: 4px;
  background: rgba(255, 255, 255, .92);
  box-shadow: 0 1px 3px rgba(34, 41, 47, .18);
  color: #6e6b7b;
  font-size: 11px;
  pointer-events: auto;
}

.dark-layout .map-ctrl__pill { background: #283046; }
.dark-layout .map-ctrl__label { color: #b4b7bd; }
.dark-layout .map-ctrl__count { background: rgba(40, 48, 70, .92); color: #b4b7bd; }

.dispatch-grid__list {
  min-height: 0;
  overflow-y: auto;
}

@media (max-width: 1199.98px) {
  .dispatch-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .dispatch-grid__canvas {
    flex: none;
    height: 40vh;
    min-height: 220px;
  }
  .dispatch-grid__list {
    overflow: visible;
  }
}

.dispatch-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  min-width: 0;
}
.dispatch-head__title { font-size: 1.05rem; font-weight: 600; color: #5e5873; }
.dispatch-head__proto {
  font-size: 12px;
  font-weight: 600;
  color: #6e6b7b;
  font-variant-numeric: tabular-nums;
}
.dispatch-head__risk {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border: 1px solid;
  border-radius: 12px;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .03em;
}
.dispatch-head__where {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  max-width: 46ch;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 12px;
  font-weight: 400;
  color: #6e6b7b;
}

.dark-layout .dispatch-head__title { color: #d0d2d6; }
.dark-layout .dispatch-head__proto,
.dark-layout .dispatch-head__where { color: #b4b7bd; }

.dispatch-bar__error { color: #ea5455; }

.radius-group {
  display: inline-flex;
  border: 1px solid #ebe9f1;
  border-radius: 5px;
  overflow: hidden;
  background: #fff;
}
.radius-group__btn {
  border: 0;
  border-left: 1px solid #ebe9f1;
  background: transparent;
  color: #6e6b7b;
  padding: 2px 10px;
  font-size: 11.5px;
  line-height: 1.6;
  cursor: pointer;
}
.radius-group__btn:first-child { border-left: 0; }
.radius-group__btn:hover { background: #f3f2f7; color: #7367f0; }
.radius-group__btn--on,
.radius-group__btn--on:hover { background: #7367f0; color: #fff; }

.dispatch-dist {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
}
.dispatch-dist__refresh {
  display: inline-flex;
  align-items: center;
  border: 1px solid #ebe9f1;
  background: #fff;
  color: #6e6b7b;
  border-radius: 4px;
  padding: 2px 7px;
  line-height: 1;
  cursor: pointer;
}
.dispatch-dist__refresh:hover { border-color: #7367f0; color: #7367f0; }
.dispatch-dist__refresh:disabled { opacity: .5; cursor: default; }

.dark-layout .radius-group,
.dark-layout .dispatch-dist__refresh { background: #283046; border-color: #3b4253; }
.dark-layout .radius-group__btn { border-left-color: #3b4253; color: #b4b7bd; }
.dark-layout .radius-group__btn:hover { background: #1c2333; }
.dark-layout .radius-group__btn--on,
.dark-layout .radius-group__btn--on:hover { background: #7367f0; color: #fff; }
.dark-layout .dispatch-dist__refresh { color: #b4b7bd; }

.team-card__dist { font-weight: 600; color: #5e5873; font-variant-numeric: tabular-nums; }
.team-card__type {
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 3px;
  background: #f3f2f7;
  color: #6e6b7b;
  font-size: 10px;
  font-weight: 600;
}
.team-card__sep { margin: 0 5px; color: #d8d6de; }

.team-card__etas {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 3px 12px;
  margin-top: 4px;
  font-size: 11px;
}
.team-card__etas-icon { color: #b9b9c3; align-self: center; }
.team-card__eta {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  white-space: nowrap;
}
.team-card__eta-label {
  color: #b9b9c3;
  font-size: 9.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: .03em;
}
.team-card__eta b {
  color: #5e5873;
  font-weight: 700;
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;
}
.team-card__eta--free span:last-child {
  color: #6e6b7b;
  font-variant-numeric: tabular-nums;
}

.dark-layout .team-card__eta b { color: #d0d2d6; }
.dark-layout .team-card__eta-label { color: #7c8194; }
.dark-layout .team-card__eta--free span:last-child { color: #b4b7bd; }

.dark-layout .team-card__type { background: #1c2333; color: #b4b7bd; }
.dark-layout .team-card__sep { color: #3b4253; }

.team-card__ring {
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 3px;
  background: #e8e6fb;
  color: #7367f0;
  font-size: 10.5px;
}
.team-card__ring--out { background: #f3f2f7; color: #b9b9c3; }

.dispatch-map__legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  margin-top: 8px;
  font-size: 11.5px;
  color: #6e6b7b;
}

.fleet-dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-right: 5px;
  vertical-align: middle;
}

.team-card--highlight {
  border-color: #7367f0 !important;
  box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.18);
}

.team-card__age { color: #b9b9c3; }

.team-list { display:flex; flex-direction:column; gap:8px; }
.team-card { display:flex; align-items:center; justify-content:space-between; padding:12px 14px; border:1px solid #ebe9f1; border-radius:8px; background:#fff; }
.team-card:hover { background:#f8f7ff; }
.team-card__name { font-size:13px; font-weight:700; color:#5e5873; margin-bottom:3px; }
.team-card__meta { display:flex; align-items:center; flex-wrap:wrap; font-size:11px; color:#b9b9c3; }
.mt-25 { margin-top:4px; }

/* dark */
.dark-layout .dispatch-card { background:#283046; border-color:#3b4253; }
.dark-layout .dispatch-card__team-name { color:#d0d2d6; }
.dark-layout .pos-timeline-wrap { border-color:#3b4253; }
.dark-layout .pos-cell { border-color:#3b4253; }
.dark-layout .pos-cell--pending  { background:#1c2333; }
.dark-layout .pos-cell--inactive { background:#1c2333; }
.dark-layout .pos-cell--pending:hover { background:#1a2a4a; }
.team-card--match { border-left: 3px solid #28c76f; padding-left: 11px; }
.team-card__badge {
  display:inline-flex; align-items:center; margin-left:6px; padding:1px 6px;
  border-radius:10px; background:#e6f9ef; color:#28c76f; font-size:10px; font-weight:700;
  text-transform:uppercase; letter-spacing:.03em;
}
.dt-no-request {
  display:flex; align-items:center; margin-bottom:12px; padding:8px 12px;
  border-radius:6px; background:#f6f6f9; color:#6e6b7b; font-size:12px;
}
.dark-layout .team-card--match { border-left-color:#28c76f; }
.dark-layout .team-card__badge { background:#173d2b; }
.dark-layout .dt-no-request { background:#1c2333; color:#b4b7bd; }
.dark-layout .team-card { background:#283046; border-color:#3b4253; }
.dark-layout .team-card:hover { background:#1c2333; }
.dark-layout .team-card__name { color:#d0d2d6; }

/* Estado vazio do modal de despacho: explica o critério em vez de só informar
   que não há equipe, o que se confunde com sistema quebrado. */
.dt-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 24px;
  color: #b9b9c3;
  max-width: 420px;
  margin: 0 auto;
}
.dt-empty h6 {
  color: #6e6b7b;
}
.dt-empty p {
  font-size: 0.84rem;
  line-height: 1.5;
}
.dark-layout .dt-empty h6 {
  color: #d0d2d6;
}
</style>
